import type { DragEventHandler, HTMLAttributeAnchorTarget, KeyboardEvent, MouseEvent, ReactNode } from 'react';

import { DataTestIds, devAssert } from '@repo/frontend-shared';
import { SDSInternalSpinner } from '@repo/sds/components/_internal/SDSInternalSpinner';
import { SDSPrimitiveButton, type SDSPrimitiveButtonProps } from '@repo/sds/components/SDSPrimitiveButton';
import type { SDSTextType } from '@repo/sds/components/SDSText';
import { SDSText } from '@repo/sds/components/SDSText';

import '@repo/sds/components/SDSButton/SDSButton.scss';

type SDSButtonMode = 'primary' | 'outline' | 'ghost' | 'soft';
type SDSButtonSize = 'small' | 'medium' | 'large';
/**
 * The semantic colourings a button can take.
 *
 * `success` is the positive counterpart of `alert`: a confirm, approve or
 * publish action. Because `status` and `mode` are independent props, it is
 * styled for every mode — primary, outline, ghost and soft — each theme naming
 * its own foreground and background, so the rendered label clears WCAG AA in
 * both; the resting brand green does not. `info` and `warning` remain typed
 * without styles, so they still change nothing on screen.
 */
type SDSStatus = 'alert' | 'info' | 'warning' | 'success';

export interface SDSButtonProps {
  className?: string;
  dataTestId?: string;
  href?: string;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  isSubmitting?: boolean;
  leftIcon?: ReactNode;
  mode?: SDSButtonMode;
  onClick?: (e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => void;
  /** Click handler for the link rendering used when `href` is set (`onClick` only fires for real buttons). */
  onLinkClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  onDragEnter?: DragEventHandler<HTMLButtonElement>;
  onDragLeave?: DragEventHandler<HTMLButtonElement>;
  onDragOver?: DragEventHandler<HTMLButtonElement>;
  onDrop?: DragEventHandler<HTMLButtonElement>;
  rightIcon?: ReactNode;
  size?: SDSButtonSize;
  status?: SDSStatus;
  text: string;
  textType?: SDSTextType;
  type?: HTMLButtonElement['type'];
  target?: HTMLAttributeAnchorTarget;
  // Accessibility props
  ariaLabel?: string;
  ariaDescribedBy?: string;
  ariaPressed?: boolean;
  ariaBusy?: boolean;
  ariaCurrent?: SDSPrimitiveButtonProps['ariaCurrent'];
}

// Determine the text type based on size
const getDefaultTextType = (size: SDSButtonSize, textType?: SDSTextType): SDSTextType => {
  if (textType) return textType;

  switch (size) {
    case 'small':
      return 'button-medium';
    case 'large':
      return 'button-x-large';
    default:
      return 'button-large';
  }
};

// Helper to build button classes
const buildButtonClass = (
  mode: SDSButtonMode,
  size: SDSButtonSize,
  isFullWidth?: boolean,
  isDisabled?: boolean,
  isSubmitting?: boolean,
  status?: SDSStatus,
  className?: string,
) => {
  const classes = ['SDSButton'];
  classes.push(`SDSButton--${mode}`);
  classes.push(`SDSButton--${size}`);
  if (isFullWidth) classes.push('SDSButton--fullWidth');
  if (isDisabled) classes.push('SDSButton--disabled');
  if (isSubmitting) classes.push('SDSButton--submitting');
  if (status) classes.push(`SDSButton--status-${status}`);
  if (className) classes.push(className);
  return classes.join(' ');
};

// Button content component
const ButtonContent = ({
  leftIcon,
  text,
  size,
  textType,
  rightIcon,
}: Pick<SDSButtonProps, 'leftIcon' | 'text' | 'size' | 'textType' | 'rightIcon'>) => (
  <div className="SDSButton__content">
    {leftIcon}
    <SDSText
      text={text}
      type={getDefaultTextType(size || 'medium', textType)}
      color="inherit"
      isInline
      className="SDSButton__text"
    />
    {rightIcon}
  </div>
);

// Build props for internal button
const buildInternalButtonProps = (
  props: SDSButtonProps,
  isDisabled: boolean,
  isSubmitting: boolean,
  buttonClass: string,
) => ({
  dataTestId: props.dataTestId ?? DataTestIds.SDS.SDS_BUTTON,
  className: buttonClass,
  isDisabled: isDisabled || isSubmitting,
  onClick: props.onClick,
  onLinkClick: props.onLinkClick,
  onDragEnter: props.onDragEnter,
  onDragLeave: props.onDragLeave,
  onDragOver: props.onDragOver,
  onDrop: props.onDrop,
  type: props.type || 'button',
  href: props.href,
  target: props.target,
  ariaLabel: props.ariaLabel || (isSubmitting ? `${props.text} (loading)` : undefined),
  ariaDescribedBy: props.ariaDescribedBy,
  ariaPressed: props.ariaPressed,
  ariaBusy: props.ariaBusy || isSubmitting,
  ariaCurrent: props.ariaCurrent,
});

export function SDSButton(props: SDSButtonProps) {
  const mode = props.mode || 'primary';
  const size = props.size || 'medium';
  const isSubmitting = props.isSubmitting ?? false;
  const isDisabled = props.isDisabled ?? false;

  // This ensures easier handling of styles when
  // the button is disabled, such as when isSubmitting is true.
  devAssert(isSubmitting && isDisabled, 'SDSButton', 'isSubmitting cannot be true when isDisabled is true.');

  const buttonClass = buildButtonClass(
    mode,
    size,
    props.isFullWidth,
    isDisabled,
    isSubmitting,
    props.status,
    props.className,
  );
  const buttonProps = buildInternalButtonProps(props, isDisabled, isSubmitting, buttonClass);

  return (
    <SDSPrimitiveButton {...buttonProps}>
      {isSubmitting && (
        <div className="SDSButton__spinner">
          <SDSInternalSpinner />
        </div>
      )}
      <ButtonContent
        leftIcon={props.leftIcon}
        text={props.text}
        size={size}
        textType={props.textType}
        rightIcon={props.rightIcon}
      />
    </SDSPrimitiveButton>
  );
}
